/**
 * Activity Console / Approvals desk — action kind, display names, buttons.
 * Matches product matrix: Approval / Checklist / Letter / Acknowledge / Helpdesk.
 */

import { parentActionLabel } from "./deskRequestCancellation";
import {
  PAYROLL_REIMB_CLAIM_LABEL,
  PAYROLL_REIMB_OPTIN_LABEL,
} from "./flexiReimbursementDesk";

const LETTER_NAME_RE =
  /\b(letter|relieving|experience\s*cert|appointment\s*letter|welcome\s*letter|fnf\s*letter|generate\s*letter)\b/i;

export const RESIGNATION_APPROVAL_LABEL = "Resignation Approval";
export const TERMINATION_APPROVAL_LABEL = "Termination / Separation Approval";
/** @deprecated Prefer RESIGNATION_APPROVAL_LABEL / TERMINATION_APPROVAL_LABEL */
export const EXIT_SEPARATION_APPROVAL_LABEL = "Exit / Separation Approval";

export function isLetterChecklistName(name) {
  return LETTER_NAME_RE.test(String(name || ""));
}

export function resolveSeparationApprovalLabel(separationType) {
  const t = String(separationType || "").toLowerCase();
  if (t === "termination") return TERMINATION_APPROVAL_LABEL;
  return RESIGNATION_APPROVAL_LABEL;
}

export function resolveChecklistDisplayLabel({
  lifecycleKind,
  separationType,
  taskName,
  actionLabel,
}) {
  const letter = isLetterChecklistName(taskName);
  if (lifecycleKind === "confirmation") {
    return letter ? "Confirmation Letter Task" : "Confirmation Task";
  }
  if (lifecycleKind === "transfer") {
    return letter ? "Transfer Letter Task" : "Transfer Task";
  }
  if (lifecycleKind === "separation") {
    const term = String(separationType || "").toLowerCase() === "termination";
    if (letter) {
      return term ? "Termination Letter Task" : "Resignation Letter Task";
    }
    // Clearance / separation checklist step
    return term ? "Termination Task" : "Resignation Task";
  }
  if (actionLabel === "Onboarding Task" || lifecycleKind === "onboarding") {
    return letter ? "Onboarding Letter Task" : "Onboarding Task";
  }
  return actionLabel || "Checklist Task";
}

/**
 * @returns {'approval'|'checklist'|'letter'|'acknowledge'|'helpdesk'|'request'}
 */
export function resolveDeskActionKind(row) {
  if (!row) return "approval";
  if (row.notifyOnlyFyi || row.deskMode === "notify") return "acknowledge";
  if (row.deskMode === "helpdesk" || row.actionLabel === "Ticket") return "helpdesk";
  if (row.deskMode === "request") return "request";
  if (row.deskMode === "checklist") {
    const taskName =
      row.formData?.taskName || row.checklistTask?.name || row.checklistName || "";
    if (isLetterChecklistName(taskName) || /letter task$/i.test(String(row.actionLabel || ""))) {
      return "letter";
    }
    return "checklist";
  }
  return "approval";
}

/**
 * Primary action buttons for a pending desk item.
 * status values stay Approved / Rejected / Undo / Acknowledged for API routing.
 */
export function getDeskActionButtons(row) {
  if (!row || row.status !== "Pending" || row.readOnlyApproval) return [];

  if (
    row.deskMode === "flexi_claim" ||
    row.actionLabel === PAYROLL_REIMB_CLAIM_LABEL
  ) {
    if (row.canAct === true || row.canApprove === true) {
      return [
        { status: "Approved", label: "Approve" },
        { status: "Rejected", label: "Reject" },
        { status: "Query", label: "Query" },
      ];
    }
    return [];
  }
  if (
    row.deskMode === "flexi_optin" ||
    row.actionLabel === PAYROLL_REIMB_OPTIN_LABEL
  ) {
    return [];
  }

  const kind = resolveDeskActionKind(row);
  if (kind === "acknowledge") {
    return [{ status: "Acknowledged", label: "Acknowledge" }];
  }
  if (kind === "letter") {
    return [
      { status: "Approved", label: "Mark Generated" },
      { status: "Rejected", label: "Reject" },
    ];
  }
  if (kind === "checklist") {
    return [
      { status: "Approved", label: "Done" },
      { status: "Rejected", label: "Reject" },
    ];
  }
  if (kind === "helpdesk") {
    return [
      { status: "Approved", label: "Resolve" },
      { status: "Rejected", label: "Reject" },
    ];
  }
  if (kind === "request") {
    const apiStatus = String(row.apiStatus || "").toLowerCase();
    // Leftover draft without RM — only Recall. With RM, backend auto-routes on desk load.
    if (row.lifecycleKind === "onboarding" && apiStatus === "draft") {
      return [{ status: "Undo", label: "Recall" }];
    }
    // Onboarding My requests: Approve/Reject when caller can act (RM / Admin).
    if (
      row.lifecycleKind === "onboarding" &&
      (row.canApprove || row.canAct) &&
      row.status === "Pending" &&
      apiStatus !== "draft"
    ) {
      return [
        { status: "Approved", label: "Approve" },
        { status: "Rejected", label: "Reject" },
      ];
    }
    // Waiting-on-others: owner may only Recall (withdraw).
    return [{ status: "Undo", label: "Recall" }];
  }
  // approval — Employee Onboarding Approval: Approve/Reject (Admin/RM act here)
  if (
    row.actionLabel === "Employee Onboarding Approval" ||
    row.actionLabel === "Employee Onboarding"
  ) {
    return [
      { status: "Approved", label: "Approve" },
      { status: "Rejected", label: "Reject" },
    ];
  }
  return [
    { status: "Approved", label: "Approve" },
    { status: "Rejected", label: "Reject" },
    { status: "Undo", label: "Recall" },
  ];
}

export function getDeskRemarksLabel(row) {
  const kind = resolveDeskActionKind(row);
  if (kind === "checklist" || kind === "letter") return "Task comments";
  if (kind === "helpdesk") return "Comments";
  if (kind === "acknowledge") return null;
  if (
    kind === "request" &&
    String(row?.apiStatus || "").toLowerCase() === "draft"
  ) {
    return null;
  }
  return "Remarks";
}

export function remarksRequiredForAction(row, nextStatus) {
  const kind = resolveDeskActionKind(row);
  if (kind === "acknowledge") return false;
  if (nextStatus === "Submitted") return false;
  if (kind === "checklist" || kind === "letter") {
    return nextStatus === "Approved" || nextStatus === "Rejected";
  }
  return true;
}

export function getChecklistNameHighlight(row) {
  if (!row) return null;
  const kind = resolveDeskActionKind(row);
  if (kind !== "checklist" && kind !== "letter") return null;
  return (
    row.formData?.taskName ||
    row.checklistTask?.name ||
    row.checklistName ||
    null
  );
}

/** Internal / already-shown keys — never surface in Applied form data. */
export const DESK_FORM_HIDDEN_KEYS = new Set([
  "employeeCode",
  "employee_code",
  "empCode",
  "employeeName",
  "employee_name",
  "userName",
  "userEmail",
  "status",
  "pendingWith",
  "pendingAt",
  "workflowKey",
  "workflow_key",
  "linkedEmployeeId",
  "linked_employee_id",
  "employeeId",
  "candidateId",
  "actionRole",
  "action_role",
  "mandatory",
  "requestType",
  "request_type",
  "deskMode",
  "lifecycleKind",
  "checklistItemId",
  "checklistTaskId",
  "triggerEvent",
  "slaPending",
  "id",
  "requestId",
  // Attendance / RH internals + shared-draft pollution
  "holidayId",
  "rhKey",
  "holidayDate",
  "ticketPriority",
  "ticketType",
  "ticketCategory",
  "ticketSubcategory",
  "ticketSubject",
  "ticketNo",
  "taxRegime",
  "investmentSection",
  "financialYear",
  "amount",
  "positionsCount",
  "positionTitle",
  "requiredByDate",
  "requestDepartment",
  "requestDesignation",
  "leaveDuration",
  "leaveType",
  "priority",
  "year",
  "month",
  "day",
  "ledgerId",
  "sourceDate",
  "checkIn",
  "checkOut",
  "checkInTime",
  "checkOutTime",
]);

/**
 * Business-facing form fields for Activity Console / Approvals detail.
 * Hides technical metadata and values already implied by the card title.
 */
export function getDeskAppliedFormFields(row, options = {}) {
  const fd = row?.formData || {};
  const action = parentActionLabel(row?.actionLabel) || String(row?.actionLabel || "");
  const hideChecklistName = options.hideChecklistName !== false;

  if (action === PAYROLL_REIMB_CLAIM_LABEL || row?.deskMode === "flexi_claim") {
    const fields = [];
    if (fd.componentCode) {
      fields.push({ key: "componentCode", label: "Component", value: String(fd.componentCode) });
    }
    if (fd.claimAmount != null && fd.claimAmount !== "") {
      fields.push({
        key: "claimAmount",
        label: "Claim amount",
        value: formatDeskFormFieldValue("claimAmount", fd.claimAmount),
      });
    }
    if (fd.claimPeriodFrom || fd.claimPeriodTo) {
      fields.push({
        key: "claimPeriod",
        label: "Period",
        value: `${String(fd.claimPeriodFrom || "—").slice(0, 10)} → ${String(fd.claimPeriodTo || "—").slice(0, 10)}`,
      });
    }
    if (fd.notes) fields.push({ key: "notes", label: "Notes", value: String(fd.notes) });
    if (fd.remarks) fields.push({ key: "remarks", label: "Remarks", value: String(fd.remarks) });
    return fields;
  }

  if (action === PAYROLL_REIMB_OPTIN_LABEL || row?.deskMode === "flexi_optin") {
    const fields = [];
    if (fd.componentCode) {
      fields.push({ key: "componentCode", label: "Component", value: String(fd.componentCode) });
    }
    if (fd.declaredAmount != null && fd.declaredAmount !== "") {
      fields.push({
        key: "declaredAmount",
        label: "Opt-in amount",
        value: formatDeskFormFieldValue("declaredAmount", fd.declaredAmount),
      });
    }
    if (fd.structureAnnualMax != null && fd.structureAnnualMax !== "") {
      fields.push({
        key: "structureAnnualMax",
        label: "Structure max (FY)",
        value: formatDeskFormFieldValue("structureAnnualMax", fd.structureAnnualMax),
      });
    }
    if (fd.financialYear) {
      fields.push({ key: "financialYear", label: "Financial year", value: String(fd.financialYear) });
    }
    if (fd.remarks) fields.push({ key: "remarks", label: "Remarks", value: String(fd.remarks) });
    return fields;
  }

  // Restricted Holiday — Leave-style subset only (never dump shared draft leftovers)
  if (action === "Restricted Holiday") {
    const fields = [];
    const holidayName = fd.holidayName || fd.reason || "";
    const date = String(fd.date || fd.holidayDate || "").slice(0, 10);
    if (holidayName) {
      fields.push({
        key: "holidayName",
        label: "Holiday",
        value: String(holidayName),
      });
    }
    if (date) {
      fields.push({
        key: "date",
        label: "Date",
        value: formatDeskFormFieldValue("date", date),
      });
    }
    if (fd.reason && String(fd.reason).trim() !== String(holidayName).trim()) {
      fields.push({
        key: "reason",
        label: "Reason",
        value: String(fd.reason),
      });
    }
    if (fd.notes) {
      fields.push({
        key: "notes",
        label: "Notes",
        value: String(fd.notes),
      });
    }
    return fields;
  }

  // Regularise checks — Leave-style key fields only
  if (action === "Regularise checks") {
    const fields = [];
    const date = String(fd.date || "").slice(0, 10);
    const inTime = fd.inTime || fd.checkInTime || fd.checkIn || "";
    const outTime = fd.outTime || fd.checkOutTime || fd.checkOut || "";
    if (date) {
      fields.push({ key: "date", label: "Date", value: formatDeskFormFieldValue("date", date) });
    }
    if (inTime) fields.push({ key: "inTime", label: "In Time", value: String(inTime) });
    if (outTime) fields.push({ key: "outTime", label: "Out Time", value: String(outTime) });
    if (fd.totalHours) {
      fields.push({ key: "totalHours", label: "Total Hours", value: String(fd.totalHours) });
    }
    if (fd.reason) fields.push({ key: "reason", label: "Reason", value: String(fd.reason) });
    if (fd.notes) fields.push({ key: "notes", label: "Notes", value: String(fd.notes) });
    return fields;
  }

  if (action === "Comp Off Credit") {
    const fields = [];
    const date = String(fd.date || fd.sourceDate || "").slice(0, 10);
    if (date) {
      fields.push({ key: "date", label: "Worked Date", value: formatDeskFormFieldValue("date", date) });
    }
    if (fd.hoursWorked != null && fd.hoursWorked !== "") {
      fields.push({ key: "hoursWorked", label: "Hours Worked", value: String(fd.hoursWorked) });
    }
    if (fd.creditDays != null && fd.creditDays !== "") {
      fields.push({ key: "creditDays", label: "Credit Days", value: String(fd.creditDays) });
    }
    if (fd.reason) fields.push({ key: "reason", label: "Reason", value: String(fd.reason) });
    if (fd.notes) fields.push({ key: "notes", label: "Notes", value: String(fd.notes) });
    return fields;
  }

  // Onboarding / checklist — Leave-style key fields only (person is on the header)
  if (
    row?.deskMode === "checklist" ||
    /Task$/i.test(action) ||
    action === "Employee Onboarding Approval" ||
    action === "Employee Onboarding"
  ) {
    const fields = [];
    const taskName = fd.taskName || fd.checklistName || "";
    if (taskName && options.hideChecklistName === false) {
      fields.push({ key: "taskName", label: "Checklist", value: String(taskName) });
    }
    if (fd.employmentType) {
      fields.push({
        key: "employmentType",
        label: "Employment Type",
        value: String(fd.employmentType),
      });
    }
    if (fd.dateOfJoin) {
      fields.push({
        key: "dateOfJoin",
        label: "Date of Join",
        value: formatDeskFormFieldValue("dateOfJoin", fd.dateOfJoin),
      });
    }
    if (fd.department) {
      fields.push({ key: "department", label: "Department", value: String(fd.department) });
    }
    if (fd.designation) {
      fields.push({
        key: "designation",
        label: "Designation",
        value: String(fd.designation),
      });
    }
    if (fd.branch) {
      fields.push({ key: "branch", label: "Branch", value: String(fd.branch) });
    }
    if (fd.reportingManager) {
      fields.push({
        key: "reportingManager",
        label: "Reporting Manager",
        value: String(fd.reportingManager),
      });
    }
    if (fd.dueAt) {
      fields.push({
        key: "dueAt",
        label: "Due Date",
        value: formatDeskFormFieldValue("dueAt", fd.dueAt),
      });
    }
    if (fd.completedComments) {
      fields.push({
        key: "completedComments",
        label: "Comments",
        value: String(fd.completedComments),
      });
    }
    if (fd.sentBackComments) {
      fields.push({
        key: "sentBackComments",
        label: "Rejection remarks",
        value: String(fd.sentBackComments),
      });
    }
    return fields;
  }
  const preferredOrder = [
    "date",
    "fromDate",
    "toDate",
    "leaveType",
    "leaveDuration",
    "currentShift",
    "shift",
    "inTime",
    "outTime",
    "totalHours",
    "startTime",
    "endTime",
    "ticketNo",
    "ticketType",
    "ticketCategory",
    "ticketSubcategory",
    "ticketPriority",
    "ticketSubject",
    "separationDate",
    "lastWorkingDay",
    "effectiveDate",
    "expectedConfirmationDate",
    "probationEndDate",
    "transferType",
    "policyName",
    "department",
    "assignedTo",
    "dueAt",
    "reason",
    "notes",
    "completedComments",
    "sentBackComments",
  ];

  const titleImpliesSeparationType =
    /resignation|termination/i.test(String(row?.actionLabel || ""));

  const keys = Object.keys(fd).filter((k) => {
    if (DESK_FORM_HIDDEN_KEYS.has(k)) return false;
    if (hideChecklistName && (k === "taskName" || k === "checklistName")) return false;
    if (k === "checklistName" && fd.taskName) return false;
    if (k === "separationType" && titleImpliesSeparationType) return false;
    const val = fd[k];
    if (val == null || String(val).trim() === "") return false;
    // Skip noisy booleans / raw ids
    if (typeof val === "boolean") return false;
    if (typeof val === "object") return false;
    return true;
  });

  keys.sort((a, b) => {
    const ia = preferredOrder.indexOf(a);
    const ib = preferredOrder.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return keys.map((key) => ({
    key,
    label: DESK_FORM_FIELD_LABELS[key] || humanizeDeskFormKey(key),
    value: formatDeskFormFieldValue(key, fd[key]),
  }));
}

const DESK_FORM_FIELD_LABELS = {
  date: "Effective Date",
  fromDate: "From Date",
  toDate: "To Date",
  leaveType: "Leave Type",
  leaveDuration: "Duration",
  currentShift: "Current Shift",
  shift: "New Shift",
  inTime: "In Time",
  outTime: "Out Time",
  totalHours: "Total Hours",
  startTime: "Start Time",
  endTime: "End Time",
  reason: "Reason",
  notes: "Notes",
  componentCode: "Component",
  claimAmount: "Claim amount",
  declaredAmount: "Opt-in amount",
  structureAnnualMax: "Structure max (FY)",
  claimPeriodFrom: "Period from",
  claimPeriodTo: "Period to",
  financialYear: "Financial year",
  ticketType: "Type",
  ticketCategory: "Category",
  ticketSubcategory: "Subcategory",
  ticketPriority: "Priority",
  ticketSubject: "Subject",
  ticketNo: "Ticket No",
  separationDate: "Separation Date",
  lastWorkingDay: "Last Working Day",
  effectiveDate: "Effective Date",
  expectedConfirmationDate: "Expected Confirmation",
  probationEndDate: "Probation End",
  transferType: "Transfer Type",
  policyName: "Policy",
  department: "Department",
  assignedTo: "Assigned To",
  dueAt: "Due Date",
  completedComments: "Prior Comments",
  sentBackComments: "Send-back Comments",
};

function humanizeDeskFormKey(key) {
  return String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

const DESK_MONTH_MMM = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Display date as `05 Sep 2026` (stored value unchanged). */
export function formatDeskDisplayDate(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${String(value.getDate()).padStart(2, "0")} ${DESK_MONTH_MMM[value.getMonth()]} ${value.getFullYear()}`;
  }
  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const monthIdx = Number(iso[2]) - 1;
    if (monthIdx >= 0 && monthIdx < 12) {
      return `${iso[3]} ${DESK_MONTH_MMM[monthIdx]} ${iso[1]}`;
    }
  }
  const dmy = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) {
    const monthIdx = Number(dmy[2]) - 1;
    if (monthIdx >= 0 && monthIdx < 12) {
      return `${dmy[1]} ${DESK_MONTH_MMM[monthIdx]} ${dmy[3]}`;
    }
  }
  const d = new Date(text);
  if (!Number.isNaN(d.getTime())) {
    return `${String(d.getDate()).padStart(2, "0")} ${DESK_MONTH_MMM[d.getMonth()]} ${d.getFullYear()}`;
  }
  return text;
}

/** Display timestamp as `05 Sep 2026, 1:19 pm`. Date-only ISO stays date-only. */
export function formatDeskDisplayDateTime(value) {
  if (value == null || value === "") return "";
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text) || /^\d{2}-\d{2}-\d{4}$/.test(text)) {
    return formatDeskDisplayDate(text);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return text;
  const time = d
    .toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
  return `${formatDeskDisplayDate(d)}, ${time}`;
}

function formatDeskFormFieldValue(key, value) {
  if (value == null || value === "") return "-";
  const text = String(value).trim();
  if (key === "transferType" || key === "leaveDuration") {
    return text.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (
    /^\d{4}-\d{2}-\d{2}/.test(text) ||
    /^\d{2}-\d{2}-\d{4}$/.test(text)
  ) {
    return formatDeskDisplayDate(text) || text;
  }
  return text;
}
